import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useOutsideClick,
  useColorModeValue,
  IconButton,
  HStack,
  Icon
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Calendar, X } from "lucide-react";

// Utility functions for date manipulation
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

// Format date to YYYY-MM-DD
const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

// Parse date from YYYY-MM-DD
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Check if in correct format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null;
  
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  
  // Validate the date is correct (handles invalid dates like 2023-02-31)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  
  return date;
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June", 
  "July", "August", "September", "October", "November", "December"
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CustomDateSelector = ({
  value = "",
  onChange,
  placeholder = "Select date",
  dateFormat = "DD-MM-YYYY",
  size = "md",
  borderRadius = "md",
  borderColor = "#cccccc",
  iconColor = "#cccccc",
  bg = "white",
  _hover = { borderColor: "#b3b3b3" },
  _focus = { borderColor: "#3182ce" },
  minDate,
  maxDate,
  ...rest
}) => {
  // State for calendar
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [displayMonth, setDisplayMonth] = useState(new Date().getMonth());
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [inputValue, setInputValue] = useState("");
  
  const calendarRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize with parsed min and max dates
  const parsedMinDate = minDate ? (typeof minDate === 'string' ? parseDate(minDate) : new Date(minDate)) : null;
  const parsedMaxDate = maxDate ? (typeof maxDate === 'string' ? parseDate(maxDate) : new Date(maxDate)) : null;

  // Close the calendar when clicking outside
  useOutsideClick({
    ref: calendarRef,
    handler: () => setIsOpen(false),
  });

  // Update selectedDate when value prop changes
  useEffect(() => {
    if (value) {
      // Handle string value (YYYY-MM-DD format)
      if (typeof value === 'string') {
        const date = parseDate(value);
        if (date && !isNaN(date.getTime())) {
          setSelectedDate(date);
          setDisplayMonth(date.getMonth());
          setDisplayYear(date.getFullYear());
          setInputValue(value);
        } else {
          setInputValue(value);
        }
      } 
      // Handle Date object
      else if (value instanceof Date && !isNaN(value.getTime())) {
        setSelectedDate(value);
        setDisplayMonth(value.getMonth());
        setDisplayYear(value.getFullYear());
        setInputValue(formatDate(value));
      }
    } else {
      setSelectedDate(null);
      setInputValue("");
    }
  }, [value]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Call onChange with a synthetic event containing target.value
    // to match the behavior of a standard input
    onChange && onChange({ target: { value } });
    
    // If valid date format, update the selected date
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const date = parseDate(value);
      if (date && !isNaN(date.getTime())) {
        // Check if date is within min and max constraints
        const isValid = isDateInRange(date.getDate(), date.getMonth(), date.getFullYear());
        
        if (isValid) {
          setSelectedDate(date);
          setDisplayMonth(date.getMonth());
          setDisplayYear(date.getFullYear());
        }
      }
    } else if (value === "") {
      // Handle clearing the input
      setSelectedDate(null);
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (day) => {
    const newDate = new Date(displayYear, displayMonth, day);
    const formattedDate = formatDate(newDate);
    setSelectedDate(newDate);
    setInputValue(formattedDate);
    
    // Call onChange with a synthetic event containing target.value
    onChange && onChange({ target: { value: formattedDate } });
    
    setIsOpen(false);
  };

  // Navigate to previous month
  const prevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  // Navigate to next month
  const nextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if date is in range
  const isDateInRange = (day, month = displayMonth, year = displayYear) => {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);
    
    if (parsedMinDate) {
      const min = new Date(parsedMinDate);
      min.setHours(0, 0, 0, 0);
      if (date < min) return false;
    }
    
    if (parsedMaxDate) {
      const max = new Date(parsedMaxDate);
      max.setHours(0, 0, 0, 0);
      if (date > max) return false;
    }
    
    return true;
  };

  // Clear the date
  const clearDate = (e) => {
    e.stopPropagation();
    setSelectedDate(null);
    setInputValue("");
    
    // Call onChange with empty value
    onChange && onChange({ target: { value: "" } });
  };

  // Generate the calendar grid
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(displayYear, displayMonth);
    const firstDayOfMonth = getFirstDayOfMonth(displayYear, displayMonth);
    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Box key={`empty-${i}`} />);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayYear, displayMonth, day);
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === displayMonth && 
        selectedDate.getFullYear() === displayYear;
      
      const isToday = 
        today.getDate() === day && 
        today.getMonth() === displayMonth && 
        today.getFullYear() === displayYear;
      
      const inRange = isDateInRange(day);

      days.push(
        <Box
          key={day}
          textAlign="center"
          py={1}
          px={1}
          cursor={inRange ? "pointer" : "not-allowed"}
          onClick={() => inRange && handleDateSelect(day)}
          bg={isSelected ? "blue.500" : isToday ? "blue.50" : "transparent"}
          color={isSelected ? "white" : !inRange ? "gray.300" : "inherit"}
          borderRadius="md"
          _hover={inRange ? { bg: isSelected ? "blue.600" : "blue.100" } : {}}
          fontWeight={isToday ? "bold" : "normal"}
        >
          {day}
        </Box>
      );
    }

    return days;
  };

  // Size mappings
  const sizeMap = {
    xs: { height: "6", fontSize: "xs", iconSize: 14 },
    sm: { height: "8", fontSize: "sm", iconSize: 16 },
    md: { height: "10", fontSize: "md", iconSize: 18 },
    lg: { height: "12", fontSize: "lg", iconSize: 20 },
  };

  const currentSize = sizeMap[size] || sizeMap.md;
  const activeBgColor = useColorModeValue("gray.100", "gray.600");

  // Jump to today
  const goToToday = () => {
    const today = new Date();
    setDisplayMonth(today.getMonth());
    setDisplayYear(today.getFullYear());
    if (isDateInRange(today.getDate(), today.getMonth(), today.getFullYear())) {
      setSelectedDate(today);
      const formattedDate = formatDate(today);
      setInputValue(formattedDate);
      
      // Call onChange with today's date
      onChange && onChange({ target: { value: formattedDate } });
    }
  };

  // Update the setShowYearView function to ensure proper centering
  const handleYearViewOpen = () => {
    // Ensure the current display year is centered in the years grid
    const yearsPerPage = 12;
    const halfRange = Math.floor(yearsPerPage / 2);
    
    // Calculate the appropriate starting year for the grid
    // This ensures the displayYear is in the middle of the grid
    let yearGridBase = Math.floor(displayYear / yearsPerPage) * yearsPerPage;
    
    // Adjust if necessary to center the current year
    if (displayYear - yearGridBase > halfRange) {
      yearGridBase = yearGridBase + yearsPerPage - (2 * halfRange);
    }
    
    // Set the display year to the base + half range to center the grid
    setDisplayYear(prevYear => {
      // Only adjust if it would shift the view significantly
      const newBase = Math.floor(prevYear / yearsPerPage) * yearsPerPage;
      if (Math.abs(newBase - yearGridBase) > halfRange) {
        return yearGridBase + halfRange;
      }
      return prevYear;
    });
    
    setShowYearView(true);
    setShowMonthView(false);
  };

  // Navigation to specific year or month
  const [showYearView, setShowYearView] = useState(false);
  const [showMonthView, setShowMonthView] = useState(false);

  const renderYearSelector = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    
    // Calculate year range with better bounds
    const yearsPerPage = 12;
    const halfRange = Math.floor(yearsPerPage / 2);
    
    // Center around display year with proper bounds
    let startYear = displayYear - halfRange;
    const endYear = startYear + yearsPerPage - 1;
    
    // Handle previous/next year navigation
    const goToPrevYears = () => {
      setDisplayYear(prevYear => {
        const newYear = prevYear - yearsPerPage;
        return Math.max(1900, newYear); // Reasonable lower bound
      });
    };
    
    const goToNextYears = () => {
      setDisplayYear(prevYear => {
        const newYear = prevYear + yearsPerPage;
        return Math.min(2100, newYear); // Reasonable upper bound
      });
    };
    
    for (let i = 0; i < yearsPerPage; i++) {
      const year = startYear + i;
      if (year < 1900) continue; // Skip years before 1900
      if (year > 2100) break;    // Don't go beyond 2100
      
      years.push(
        <Button
          key={year}
          variant={year === displayYear ? "solid" : "ghost"}
          colorScheme={year === displayYear ? "blue" : "gray"}
          onClick={() => {
            setDisplayYear(year);
            setShowYearView(false);
            setShowMonthView(true);
          }}
          size="sm"
        >
          {year}
        </Button>
      );
    }
    
    return (
      <Box>
        <Flex justifyContent="space-between" mb={2}>
          <IconButton 
            size="sm" 
            icon={<ChevronLeftIcon />} 
            onClick={goToPrevYears}
            aria-label="Previous years"
          />
          <Text fontWeight="bold">
            {startYear} - {endYear}
          </Text>
          <IconButton 
            size="sm" 
            icon={<ChevronRightIcon />} 
            onClick={goToNextYears}
            aria-label="Next years"
          />
        </Flex>
        <Grid templateColumns="repeat(4, 1fr)" gap={2}>
          {years}
        </Grid>
        <Button size="xs" mt={3} variant="ghost" onClick={() => setIsOpen(false)}>
          Close
        </Button>
      </Box>
    );
  };

  const renderMonthSelector = () => {
    return (
      <Box>
        <Flex justifyContent="space-between" mb={2}>
          <Text fontWeight="bold">Select Month</Text>
          <Button size="xs" onClick={handleYearViewOpen}>Years</Button>
        </Flex>
        <Grid templateColumns="repeat(3, 1fr)" gap={2}>
          {MONTHS.map((month, index) => (
            <Button
              key={month}
              variant={index === displayMonth ? "solid" : "ghost"}
              colorScheme={index === displayMonth ? "blue" : "gray"}
              onClick={() => {
                setDisplayMonth(index);
                setShowMonthView(false);
              }}
              size="sm"
            >
              {month.substring(0, 3)}
            </Button>
          ))}
        </Grid>
        <Button size="xs" mt={3} variant="ghost" onClick={() => setIsOpen(false)}>
          Close
        </Button>
      </Box>
    );
  };

  return (
    <Box position="relative" width="100%" ref={calendarRef} {...rest}>
      <Popover
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setShowYearView(false);
          setShowMonthView(false);
        }}
        placement="bottom-start"
        autoFocus={false}
        closeOnBlur={false}
        isLazy
      >
        <PopoverTrigger>
          <InputGroup size={size}>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onClick={() => setIsOpen(true)}
              placeholder={placeholder}
              height={currentSize.height}
              fontSize={currentSize.fontSize}
              bg={bg}
              borderRadius={borderRadius}
              borderColor={isOpen ? _focus.borderColor : borderColor}
              _hover={_hover}
              _focus={{ ...(_focus || {}), outline: "none" }}
              autoComplete="off"
              style={{
                caretColor: 'transparent'
              }}
            />
            <InputRightElement width="auto" color={iconColor} pr={2} h="100%">
              <Flex alignItems="center" gap={1} pointerEvents="auto">
                {selectedDate && (
                  <Box
                    as="span"
                    cursor="pointer"
                    onClick={clearDate}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ color: 'black' }}
                  >
                    <Icon as={X} boxSize={`${currentSize.iconSize}px`} />
                  </Box>
                )}
                <Box
                  cursor="pointer"
                  onClick={() => setIsOpen(!isOpen)}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ color: 'black' }}
                >
                  <Icon as={Calendar} boxSize={`${currentSize.iconSize}px`} />
                </Box>
              </Flex>
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent width="280px" p={3} borderRadius="md" boxShadow="lg">
          {showYearView ? (
            renderYearSelector()
          ) : showMonthView ? (
            renderMonthSelector()
          ) : (
            <>
              <Flex justifyContent="space-between" alignItems="center" mb={3}>
                <IconButton
                  size="sm"
                  icon={<ChevronLeftIcon />}
                  onClick={prevMonth}
                  aria-label="Previous month"
                  variant="ghost"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  fontWeight="bold"
                  onClick={() => setShowMonthView(true)}
                >
                  {MONTHS[displayMonth]} {displayYear}
                </Button>
                <IconButton
                  size="sm"
                  icon={<ChevronRightIcon />}
                  onClick={nextMonth}
                  aria-label="Next month"
                  variant="ghost"
                />
              </Flex>

              <Grid templateColumns="repeat(7, 1fr)" mb={2}>
                {DAYS.map((day) => (
                  <Box key={day} textAlign="center" fontWeight="bold" fontSize="sm" color="gray.500">
                    {day[0]}
                  </Box>
                ))}
              </Grid>

              <Grid templateColumns="repeat(7, 1fr)" gap={1}>
                {renderCalendarDays()}
              </Grid>

              <HStack mt={3} justifyContent="space-between">
                <Button size="xs" variant="link" colorScheme="blue" onClick={goToToday}>
                  Today
                </Button>
                <Flex>
                  <Button size="xs" variant="ghost" colorScheme="blue" onClick={handleYearViewOpen} mr={1}>
                    Year
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setIsOpen(false)}>
                    Close
                  </Button>
                </Flex>
              </HStack>
            </>
          )}
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default CustomDateSelector;